import { useState, useEffect } from 'react'
import api from '../services/api'
import { avisar } from './Avisos'

// Conferencia dos dados do cliente ao importar proposta de quem ja esta cadastrado (pedido do
// usuario, 26/10): compara o que veio no PDF com o cadastro e mostra so o que mudou.
//   - telefone e e-mail novos entram como ADICIONAIS (nunca apagam os existentes);
//   - endereco diferente: pergunta se adiciona (endereco adicional) ou substitui o principal;
//   - estado civil e profissao: atualiza se marcado.
// Uso: await conferirDadosCliente(clienteId, dadosDoPdf) — resolve depois que o usuario decide
// (ou na hora, se nao houver diferenca). Nunca lanca erro: falha so avisa e segue a importacao.
// O <ConferirClienteHost /> fica montado uma vez no App.jsx.

let host = null

const norm = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim()
const digitos = (v) => String(v || '').replace(/\D/g, '')
const PREFIXOS_RUA = new Set(['r', 'rua', 'av', 'avenida', 'al', 'alameda', 'tv', 'travessa', 'rod', 'rodovia', 'estr', 'estrada', 'pca', 'praca'])
const chaveRua = (v) => norm(v).split(' ').filter(w => w && !PREFIXOS_RUA.has(w)).join(' ')
const mesmoEndereco = (a, b) => {
  const cepA = digitos(a.cep), cepB = digitos(b.cep)
  if (cepA && cepB && cepA !== cepB) return false
  if (chaveRua(a.endereco) && chaveRua(b.endereco) && chaveRua(a.endereco) !== chaveRua(b.endereco)) return false
  if (digitos(a.numero) && digitos(b.numero) && digitos(a.numero) !== digitos(b.numero)) return false
  return !!(cepA && cepB) || !!(chaveRua(a.endereco) && chaveRua(b.endereco))
}
const textoEndereco = (e) => [[e.endereco, e.numero].filter(Boolean).join(', '), e.complemento, e.bairro, [e.cidade, e.estado].filter(Boolean).join('/'), e.cep].filter(Boolean).join(' — ')

export function diferencasCliente(cliente, d) {
  const dif = {}
  const novoEnd = { cep: d.cliente_cep || '', endereco: d.cliente_endereco || '', numero: d.cliente_numero || '', complemento: d.cliente_complemento || '', bairro: d.cliente_bairro || '', cidade: d.cliente_cidade || '', estado: d.cliente_estado || '' }
  if (novoEnd.endereco || digitos(novoEnd.cep)) {
    const conhecidos = [cliente, ...(Array.isArray(cliente.enderecos_adicionais) ? cliente.enderecos_adicionais : [])]
    if (!conhecidos.some(e => mesmoEndereco(e, novoEnd))) dif.endereco = { atual: cliente.endereco || cliente.cep ? cliente : null, novo: novoEnd }
  }
  // Telefone: compara pelos 8 ultimos digitos (ignora DDD e o 9 na frente do celular)
  const tel = digitos(d.cliente_telefone)
  if (tel.length >= 8 && !(cliente.telefones || []).some(t => digitos(t.numero).slice(-8) === tel.slice(-8))) dif.telefone = d.cliente_telefone
  const email = String(d.cliente_email || '').trim().toLowerCase()
  if (email && !(cliente.emails || []).some(e => String(e || '').trim().toLowerCase() === email)) dif.email = email
  for (const campo of ['estado_civil', 'profissao']) {
    const novo = String(d[`cliente_${campo}`] || '').trim()
    if (novo && norm(novo) !== norm(cliente[campo])) dif[campo] = { atual: cliente[campo] || '', novo }
  }
  return dif
}

export async function conferirDadosCliente(clienteId, dados) {
  if (!clienteId || !dados || !host) return
  try {
    const { data: cliente } = await api.get(`/clientes/${clienteId}`)
    const dif = diferencasCliente(cliente, dados)
    if (!Object.keys(dif).length) return
    await new Promise(resolve => host.abrir({ cliente, dif, resolve }))
  } catch (err) {
    avisar('Não foi possível conferir os dados do cliente: ' + (err.response?.data?.erro || err.message), 'atencao')
  }
}

export function ConferirClienteHost() {
  const [atual, setAtual] = useState(null)
  const [escolha, setEscolha] = useState({})
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    host = {
      abrir: (c) => {
        setEscolha({ endereco: 'adicionar', telefone: true, email: true, estado_civil: true, profissao: true })
        setAtual(c)
      },
    }
    return () => { host = null }
  }, [])

  if (!atual) return null
  const { cliente, dif } = atual
  const fechar = () => { atual.resolve(); setAtual(null) }

  const salvar = async () => {
    setSalvando(true)
    try {
      const c = { ...cliente }
      const adicionais = Array.isArray(c.enderecos_adicionais) ? [...c.enderecos_adicionais] : []
      if (dif.endereco && escolha.endereco === 'adicionar') adicionais.push({ tipo: '', ...dif.endereco.novo })
      if (dif.endereco && escolha.endereco === 'substituir') Object.assign(c, dif.endereco.novo)
      if (dif.telefone && escolha.telefone) c.telefones = [...(c.telefones || []), { numero: dif.telefone, nome: '' }]
      if (dif.email && escolha.email) c.emails = [...(c.emails || []), dif.email]
      if (dif.estado_civil && escolha.estado_civil) c.estado_civil = dif.estado_civil.novo
      if (dif.profissao && escolha.profissao) c.profissao = dif.profissao.novo
      await api.put(`/clientes/${cliente.id}`, {
        ...c,
        data_nascimento: c.data_nascimento ? String(c.data_nascimento).slice(0, 10) : '',
        enderecos_adicionais: adicionais,
      })
      avisar('Cadastro do cliente atualizado.', 'sucesso')
      fechar()
    } catch (err) {
      avisar('Erro ao atualizar o cliente: ' + (err.response?.data?.erro || err.message), 'erro')
    } finally {
      setSalvando(false)
    }
  }

  const linha = { padding: '12px 0', borderTop: '1px solid #F1F3F5' }
  const rotulo = { fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.03em' }
  const caixa = (campo, texto) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!escolha[campo]} onChange={e => setEscolha(s => ({ ...s, [campo]: e.target.checked }))} style={{ marginTop: 2, accentColor: 'var(--primary)' }} />
      <span>{texto}</span>
    </label>
  )
  const opcaoEnd = (valor, texto) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="radio" name="opcao-endereco" checked={escolha.endereco === valor} onChange={() => setEscolha(s => ({ ...s, endereco: valor }))} style={{ accentColor: 'var(--primary)' }} />
      {texto}
    </label>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(47,47,46,0.45)', zIndex: 20002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', fontFamily: 'Manrope, sans-serif' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Dados do cliente diferentes na proposta</h3>
        <div style={{ fontSize: 13, color: '#666', margin: '4px 0 12px' }}>
          <strong>{cliente.nome}</strong> já está cadastrado. A proposta traz estes dados diferentes do cadastro — escolha o que atualizar.
        </div>

        {dif.endereco && (
          <div style={linha}>
            <div style={rotulo}>Endereço</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}><span style={{ color: '#888' }}>No cadastro:</span> {dif.endereco.atual ? textoEndereco(dif.endereco.atual) : '—'}</div>
            <div style={{ fontSize: 13, marginBottom: 8 }}><span style={{ color: '#888' }}>Na proposta:</span> <strong>{textoEndereco(dif.endereco.novo)}</strong></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {opcaoEnd('adicionar', 'Adicionar como endereço adicional')}
              {opcaoEnd('substituir', 'Substituir o endereço principal')}
              {opcaoEnd('manter', 'Não alterar')}
            </div>
          </div>
        )}
        {dif.telefone && (
          <div style={linha}>
            <div style={rotulo}>Telefone</div>
            {caixa('telefone', <>Adicionar <strong>{dif.telefone}</strong> (os atuais continuam: {(cliente.telefones || []).map(t => t.numero).filter(Boolean).join(', ') || '—'})</>)}
          </div>
        )}
        {dif.email && (
          <div style={linha}>
            <div style={rotulo}>E-mail</div>
            {caixa('email', <>Adicionar <strong>{dif.email}</strong> (os atuais continuam: {(cliente.emails || []).filter(Boolean).join(', ') || '—'})</>)}
          </div>
        )}
        {dif.estado_civil && (
          <div style={linha}>
            <div style={rotulo}>Estado civil</div>
            {caixa('estado_civil', <>Atualizar de {dif.estado_civil.atual || '—'} para <strong>{dif.estado_civil.novo}</strong></>)}
          </div>
        )}
        {dif.profissao && (
          <div style={linha}>
            <div style={rotulo}>Profissão</div>
            {caixa('profissao', <>Atualizar de {dif.profissao.atual || '—'} para <strong>{dif.profissao.novo}</strong></>)}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" className="btn btn-secondary" disabled={salvando} onClick={fechar}>Não alterar nada</button>
          <button type="button" className="btn btn-primary" disabled={salvando} onClick={salvar}>{salvando ? 'Salvando...' : 'Atualizar cadastro'}</button>
        </div>
      </div>
    </div>
  )
}
